import React, { useEffect, useState } from 'react';
import { reportsApi } from '../services/api';

function fmt(n) {
  return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(n || 0);
}

export default function Reports() {
  const [tab, setTab] = useState('revenue');
  const [revenue, setRevenue] = useState(null);
  const [productStats, setProductStats] = useState([]);
  const [vat, setVat] = useState(null);
  const [range, setRange] = useState({ from: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10), to: new Date().toISOString().slice(0, 10) });
  const [loading, setLoading] = useState(false);

  function loadRevenue() {
    setLoading(true);
    reportsApi.revenue({ ...range, group_by: 'day' }).then(setRevenue).finally(() => setLoading(false));
  }
  function loadProducts() {
    setLoading(true);
    reportsApi.products(range).then(r => setProductStats(r.data)).finally(() => setLoading(false));
  }
  function loadVat() {
    setLoading(true);
    reportsApi.vat({ year: new Date().getFullYear() }).then(setVat).finally(() => setLoading(false));
  }

  useEffect(() => {
    if (tab === 'revenue') loadRevenue();
    else if (tab === 'products') loadProducts();
    else if (tab === 'vat') loadVat();
  }, [tab, range]);

  const totalRevenue = revenue?.data?.reduce((s, d) => s + d.revenue, 0) || 0;
  const totalOrders = revenue?.data?.reduce((s, d) => s + d.orders_count, 0) || 0;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">📈 Přehledy</h1>
        <a href={reportsApi.exportUrl({ ...range, format: 'csv' })} className="btn btn-secondary">📥 Export CSV</a>
      </div>

      {/* Date range */}
      <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div className="filters">
          <input className="form-control" type="date" value={range.from} onChange={e => setRange(r => ({...r, from: e.target.value}))} />
          <span style={{ color: 'var(--text-muted)' }}>—</span>
          <input className="form-control" type="date" value={range.to} onChange={e => setRange(r => ({...r, to: e.target.value}))} />
          {[
            ['7d', '7 dní'], ['30d', '30 dní'], ['90d', '3 měsíce'], ['year', 'Rok'],
          ].map(([k, l]) => (
            <button key={k} className="btn btn-secondary btn-sm" onClick={() => {
              const days = k === '7d' ? 7 : k === '30d' ? 30 : k === '90d' ? 90 : 365;
              setRange({ from: new Date(Date.now() - days * 86400000).toISOString().slice(0, 10), to: new Date().toISOString().slice(0, 10) });
            }}>{l}</button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {[['revenue','Tržby'],['products','Produkty'],['vat','DPH']].map(([v,l]) => (
          <button key={v} className={`btn ${tab===v ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(v)}>{l}</button>
        ))}
      </div>

      {loading && <div className="loading-center"><div className="spinner" /></div>}

      {tab === 'revenue' && revenue && !loading && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
            <div className="stat-card" style={{ borderTop: '3px solid var(--blue)' }}>
              <div className="stat-value" style={{ color: 'var(--blue)' }}>{fmt(totalRevenue)}</div>
              <div className="stat-label">Celkové tržby</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--green)' }}>
              <div className="stat-value" style={{ color: 'var(--green)' }}>{totalOrders}</div>
              <div className="stat-label">Počet objednávek</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--teal)' }}>
              <div className="stat-value" style={{ color: 'var(--teal)' }}>{totalOrders > 0 ? fmt(totalRevenue / totalOrders) : '—'}</div>
              <div className="stat-label">Průměrná objednávka</div>
            </div>
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 12 }}>Tržby po dnech</div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Datum</th><th style={{ textAlign: 'right' }}>Objednávky</th><th style={{ textAlign: 'right' }}>Tržby</th></tr></thead>
                <tbody>
                  {revenue.data.map(d => (
                    <tr key={d.period}>
                      <td>{d.period}</td>
                      <td style={{ textAlign: 'right' }}>{d.orders_count}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(d.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'products' && !loading && (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead><tr><th>Produkt</th><th style={{ textAlign: 'right' }}>Prodáno ks</th><th style={{ textAlign: 'right' }}>Tržby</th><th style={{ textAlign: 'right' }}>Náklady</th><th style={{ textAlign: 'right' }}>Zisk</th></tr></thead>
              <tbody>
                {productStats.map((p, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{p.product_name}</td>
                    <td style={{ textAlign: 'right' }}>{p.total_qty}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(p.total_revenue)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{fmt(p.total_cost)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: p.total_profit > 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(p.total_profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {productStats.length === 0 && <div className="empty-state">Žádná data</div>}
          </div>
        </div>
      )}

      {tab === 'vat' && vat && !loading && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 12 }}>DPH přehled — rok {new Date().getFullYear()}</div>
          <table>
            <thead><tr><th>Sazba DPH</th><th style={{ textAlign: 'right' }}>Základ daně</th><th style={{ textAlign: 'right' }}>DPH</th><th style={{ textAlign: 'right' }}>Celkem</th></tr></thead>
            <tbody>
              {vat.vat_breakdown.map((v, i) => (
                <tr key={i}>
                  <td>{v.rate}%</td>
                  <td style={{ textAlign: 'right' }}>{fmt(v.base)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--orange)', fontWeight: 600 }}>{fmt(v.vat)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(v.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
